import { useEffect, useState } from 'react'

import { useMutationRequest } from '~/ui/hooks/utils/service/use-query-request'

import { FeatureFlagsStorage } from './feature-flags-storage'
import { type FetchAllHeimdallFlagsProps, fetchAllHeimdallFlags } from './fetch-all-heimdall-flags'
import { type FeatureFlagsAvailable } from './types'

type ResourceIdentifier = {
  [key: string]: string
}

interface UseHeimdallProps {
  identifierList: ResourceIdentifier
  bffEnabledFeatures: string[]
  subjectId?: string
  skip?: boolean
}

export function useHeimdall({ identifierList, bffEnabledFeatures, subjectId, skip = false }: UseHeimdallProps) {
  const cachedIdentifiers = FeatureFlagsStorage.get().identifiers
  const identifiers = Object.values(identifierList).filter(Boolean)

  const [isReady, setReady] = useState(skip)

  const {
    mutate,
    data: fetchedFlags = [],
    isSuccess
  } = useMutationRequest<FeatureFlagsAvailable[], unknown, true, FetchAllHeimdallFlagsProps>(
    ['fetchFeatureFlags'],
    async opts => await fetchAllHeimdallFlags(opts)
  )

  useEffect(() => {
    if (skip) return

    const upSetFlags = () => {
      const shouldUpdateCache = identifiers.every(id => cachedIdentifiers.includes(id)) === false

      if (shouldUpdateCache) {
        FeatureFlagsStorage.clear()
        mutate({ resources: identifierList, subjectId })
      } else {
        setReady(true)
      }
    }

    upSetFlags()
  }, [])

  useEffect(() => {
    const shouldPersist = isSuccess && !isReady && Boolean(fetchedFlags.length)

    if (shouldPersist) {
      const featureFlags = [...fetchedFlags, ...bffEnabledFeatures] as FeatureFlagsAvailable[]

      FeatureFlagsStorage.set({ identifiers, featureFlags })

      setReady(true)
    }
  }, [bffEnabledFeatures, fetchedFlags, identifiers, isReady, isSuccess])

  return { isReady }
}
